import { useEffect, useState } from "react";
import { Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";

const Check = ({ id, checked, onChange, children }) => (
  <label className="flex items-start gap-2 text-sm cursor-pointer" htmlFor={id}>
    <Checkbox id={id} checked={checked} onCheckedChange={(v) => onChange(!!v)} data-testid={id} className="mt-0.5" />
    <span>{children}</span>
  </label>
);

// Informativa privacy bloccante al primo accesso (e a ogni nuova versione) per tutti tranne il SuperAdmin.
export const PrivacyGate = () => {
  const { user } = useAuth();
  const [p, setP] = useState(null);
  const [read, setRead] = useState(false);
  const [mkt, setMkt] = useState(false);
  const [terzi, setTerzi] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setP(null);
    if (!user || user.role === "super_admin") return;
    api.get("/gdpr/privacy/current").then(({ data }) => setP(data)).catch(() => {});
  }, [user]);

  if (!p || p.accepted) return null;

  const accept = async () => {
    setSaving(true);
    try {
      await api.post("/gdpr/privacy/accept", { key: p.key, marketing: mkt, terzi });
      setP({ ...p, accepted: true });
      toast.success("Informativa privacy accettata");
    } catch (e) {
      toast.error(formatApiError(e));
      if (e?.response?.status === 409) api.get("/gdpr/privacy/current").then(({ data }) => setP(data));
    } finally { setSaving(false); }
  };

  return (
    <Dialog open>
      <DialogContent className="max-w-2xl [&>button]:hidden" onInteractOutside={(e) => e.preventDefault()} onEscapeKeyDown={(e) => e.preventDefault()} data-testid="privacy-gate-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><ShieldCheck className="h-5 w-5 text-emerald-600" /> Informativa sulla privacy</DialogTitle>
          <p className="text-xs text-slate-500">Versione {p.version}{p.scope === "agency" ? " (agenzia)" : ""} — per continuare devi leggerla e accettarla.</p>
        </DialogHeader>
        <div className="max-h-[45vh] overflow-y-auto whitespace-pre-wrap text-sm leading-relaxed bg-slate-50 border rounded-lg p-4" data-testid="privacy-gate-text">{p.text}</div>
        <div className="space-y-2">
          <Check id="privacy-gate-read" checked={read} onChange={setRead}><b>Ho letto l&apos;informativa</b> e acconsento al trattamento dei miei dati per le finalità indicate (obbligatorio)</Check>
          {p.ask_consents && (
            <>
              <Check id="privacy-gate-marketing" checked={mkt} onChange={setMkt}>Acconsento a ricevere comunicazioni commerciali e promozionali (facoltativo)</Check>
              <Check id="privacy-gate-terzi" checked={terzi} onChange={setTerzi}>Acconsento alla comunicazione dei dati a terzi partner per loro finalità commerciali (facoltativo)</Check>
            </>
          )}
        </div>
        <div className="flex justify-end">
          <Button onClick={accept} disabled={!read || saving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="privacy-gate-accept">
            {saving && <Loader2 className="h-4 w-4 animate-spin mr-1" />} Accetto e continuo
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
